import React, { useState, useEffect } from 'react';
import {
  Heart,
  ThumbsDown,
  Clock,
  Users,
  Star,
  Check,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import JobSkillTags from './JobSkillTags';
import ClientInfoRow from './ClientInfoRow';
import { saveJob, unsaveJob, isJobSaved } from '../services/savedJobsService';
import { getRelativeTime, getRelativeTimeFromHours, parseDateInput } from '../utils/dateUtils';

/**
 * Enhanced job card component matching Upwork's design
 */
const JobCard = ({
  job,
  onClick,
  onNotInterested,
  onSkillClick,
  onSaveToggle,
  initialSaved = false,
  skipSavedCheck = false
}) => {
  const [isSaved, setIsSaved] = useState(initialSaved);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFeedbackMenu, setShowFeedbackMenu] = useState(false);
  const [savingState, setSavingState] = useState(null); // 'saving' | 'unsaving' | null

  // Sync isSaved with initialSaved prop when it changes
  useEffect(() => {
    setIsSaved(initialSaved);
  }, [initialSaved]);

  // Check if job is saved on mount - SKIP if parent already provides saved status
  useEffect(() => {
    if (skipSavedCheck) return; // Parent knows the saved state, don't make API call
    if (job?._id && !initialSaved) {
      isJobSaved(job._id).then(setIsSaved).catch(() => setIsSaved(false));
    }
  }, [job?._id, initialSaved, skipSavedCheck]);

  // Get contract type (Hourly vs Fixed-Price)
  const getContractType = () => {
    const contractType = job.contractTerms?.contractType;
    if (!contractType) return null;
    if (contractType === 'HOURLY') return 'Hourly';
    if (contractType === 'FIXED_PRICE') return 'Fixed-Price';
    return contractType;
  };

  // Format budget/rate with contract type prefix
  const formatBudget = () => {
    const contractType = getContractType();
    const isHourly = contractType === 'Hourly';

    // For hourly jobs, show the hourly rate range
    if (isHourly) {
      const minRate = job.hourlyBudgetMin?.displayValue;
      const maxRate = job.hourlyBudgetMax?.displayValue;

      if (minRate && maxRate) {
        // Clean up displayValue (remove "$" if present to avoid double)
        const cleanMin = minRate.replace(/^\$/, '');
        const cleanMax = maxRate.replace(/^\$/, '');
        return `Hourly: $${cleanMin}-$${cleanMax}`;
      }

      // Fallback to hourlyContractTerms
      const hourlyTerms = job.contractTerms?.hourlyContractTerms;
      if (hourlyTerms?.hourlyBudgetMin && hourlyTerms?.hourlyBudgetMax) {
        return `Hourly: $${hourlyTerms.hourlyBudgetMin}-$${hourlyTerms.hourlyBudgetMax}`;
      }
    }

    // For fixed-price jobs or fallback
    const amount = job.amount || job.budget;
    if (!amount) return contractType || null;

    let formattedAmount = null;
    if (typeof amount === 'object') {
      if (amount.displayValue) formattedAmount = amount.displayValue;
      else if (amount.value) formattedAmount = `$${amount.value}`;
      else if (amount.min && amount.max) formattedAmount = `$${amount.min} - $${amount.max}`;
    } else if (typeof amount === 'number') {
      formattedAmount = `$${amount}`;
    } else if (typeof amount === 'string') {
      formattedAmount = amount;
    }

    if (formattedAmount) {
      return contractType === 'Fixed-Price' ? `Fixed-Price: ${formattedAmount}` : formattedAmount;
    }
    return contractType || null;
  };

  // Get weekly hours commitment
  const getWeeklyHours = () => {
    // First try the engagement string (e.g., "30+ hrs/week")
    if (job.engagement) {
      return job.engagement;
    }

    // Fallback to engagementType from hourlyContractTerms
    const engagementType = job.contractTerms?.hourlyContractTerms?.engagementType;
    if (engagementType === 'FULL_TIME') {
      return 'More than 30 hrs/week';
    }
    if (engagementType === 'PART_TIME') {
      return 'Less than 30 hrs/week';
    }

    return null;
  };

  // Handle save/unsave
  const handleSaveToggle = async (e) => {
    e.stopPropagation();
    if (savingState) return;

    try {
      if (isSaved) {
        setSavingState('unsaving');
        await unsaveJob(job._id);
        setIsSaved(false);
        onSaveToggle?.();
      } else {
        setSavingState('saving');
        await saveJob(job._id);
        setIsSaved(true);
        onSaveToggle?.();
      }
    } catch (error) {
      console.error('Failed to toggle save:', error);
    } finally {
      setSavingState(null);
    }
  };

  // Handle not interested
  const handleNotInterested = (reason) => {
    setShowFeedbackMenu(false);
    if (onNotInterested) {
      onNotInterested(job, reason);
    }
  };

  // Format experience level enum to human-readable
  const formatExperienceLevel = (level) => {
    if (!level) return null;
    const levelStr = typeof level === 'object' ? level.prefLabel : level;
    if (!levelStr) return null;

    // Map common enum values to readable format
    const enumMap = {
      'ENTRY_LEVEL': 'Entry Level',
      'ENTRY': 'Entry Level',
      'INTERMEDIATE': 'Intermediate',
      'EXPERT': 'Expert',
      'SENIOR': 'Senior',
      'JUNIOR': 'Junior'
    };

    // Return mapped value or format the string (SOME_VALUE -> Some Value)
    return enumMap[levelStr.toUpperCase()] ||
           levelStr.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Get experience level display
  const getExperienceLevel = () => {
    const level = job.experienceLevel || job.contractorTier;
    return formatExperienceLevel(level);
  };

  // Get duration display
  const getDuration = () => {
    const duration = job.duration || job.projectLength || job.engagement;
    if (!duration) return null;
    if (typeof duration === 'string') return duration;
    if (duration.displayValue) return duration.displayValue;
    return null;
  };

  // Get proposal count display
  const getProposalCount = () => {
    const count = job.totalApplicants || job.proposalsTier?.proposalsReceived;
    if (!count && count !== 0) return null;

    // Sometimes it's a range
    if (typeof count === 'string') return count;

    if (count === 0) return 'Be the first to apply';
    if (count < 5) return 'Less than 5';
    if (count < 10) return '5 to 10';
    if (count < 15) return '10 to 15';
    if (count < 20) return '15 to 20';
    if (count < 50) return '20 to 50';
    return '50+';
  };

  const budget = formatBudget();
  const experienceLevel = getExperienceLevel();
  const duration = getDuration();
  const weeklyHours = getWeeklyHours();
  const proposalCount = getProposalCount();
  const description = job.description || job.content || '';
  const truncatedDescription = description.length > 300 ? description.slice(0, 300) + '...' : description;
  const hoursInactive = Number(job?.relevance?.hoursInactive);
  const syncTimestamp = parseDateInput(job?.updatedAt || job?.createdAt);
  const elapsedSinceSyncHours = syncTimestamp
    ? Math.max(0, (Date.now() - syncTimestamp.getTime()) / (1000 * 60 * 60))
    : 0;
  const postedLabelFromHours = Number.isFinite(hoursInactive) && hoursInactive >= 0
    ? getRelativeTimeFromHours(hoursInactive + elapsedSinceSyncHours)
    : '';
  const dateBasedPostedLabel = getRelativeTime(job.publishedDateTime);
  const postedLabel =
    (
      (postedLabelFromHours === 'Just now' || postedLabelFromHours === 'Less than 1 hour ago') &&
      dateBasedPostedLabel &&
      dateBasedPostedLabel !== 'Just now'
    )
      ? dateBasedPostedLabel
      : (postedLabelFromHours || dateBasedPostedLabel);

  // Build "Est. Time" string combining duration and weekly hours
  const getEstimatedTime = () => {
    const parts = [];
    if (duration) parts.push(duration);
    if (weeklyHours) parts.push(weeklyHours);
    return parts.length > 0 ? parts.join(', ') : null;
  };
  const estimatedTime = getEstimatedTime();

  return (
    <div
      onClick={() => onClick?.(job)}
      className="bg-white border border-gray-200 rounded-lg p-5 hover:border-primary-300 hover:shadow-lg hover:bg-gray-50/50 transition-all cursor-pointer group"
    >
      {/* Top Row - Posted time, Featured badge, and action icons */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {postedLabel && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              Posted {postedLabel}
            </div>
          )}
          {job.applied && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <Check className="w-3 h-3 mr-1" />
              Applied
            </span>
          )}
          {job.featured && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
              <Star className="w-3 h-3 mr-1 fill-current" />
              Featured
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {/* Not Interested Button */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowFeedbackMenu(!showFeedbackMenu);
              }}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              title="Not interested"
            >
              <ThumbsDown className="w-4 h-4" />
            </button>

            {/* Feedback Dropdown */}
            {showFeedbackMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                {[
                  'Not interested',
                  'Vague description',
                  'Unrealistic expectations',
                  'Too many applicants',
                  'Budget too low',
                  'Other'
                ].map((reason) => (
                  <button
                    key={reason}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNotInterested(reason);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {reason}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Save Button */}
          <button
            onClick={handleSaveToggle}
            disabled={savingState !== null}
            className={`p-2 rounded-full transition-colors ${
              isSaved
                ? 'text-red-500 hover:text-red-600 hover:bg-red-50'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
            title={isSaved ? 'Unsave job' : 'Save job'}
          >
            <Heart className={`w-4 h-4 ${isSaved ? 'fill-current' : ''}`} />
          </button>
        </div>
      </div>

      {/* Job Title */}
      <h3 className="text-base font-semibold text-gray-900 group-hover:text-secondary-600 group-hover:underline cursor-pointer mb-1.5 line-clamp-2 transition-colors">
        {job.title}
      </h3>

      {/* Meta Row - Rate, Experience, Est. Time */}
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mb-2">
        {budget && (
          <span>{budget}</span>
        )}
        {experienceLevel && (
          <>
            <span className="text-gray-400">-</span>
            <span>{experienceLevel}</span>
          </>
        )}
        {estimatedTime && (
          <>
            <span className="text-gray-400">-</span>
            <span>Est. Time: {estimatedTime}</span>
          </>
        )}
      </div>

      {/* Description */}
      <div className="mb-2">
        <p className={`text-sm text-gray-600 leading-relaxed ${isExpanded ? 'whitespace-pre-wrap' : ''}`}>
          {isExpanded ? description : truncatedDescription}
        </p>
        {description.length > 300 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="inline-flex items-center gap-1 text-sm text-secondary-600 hover:text-secondary-700 mt-1"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                more
              </>
            )}
          </button>
        )}
      </div>

      {/* Skills */}
      {job.skills && job.skills.length > 0 && (
        <div className="mb-2">
          <JobSkillTags
            skills={job.skills}
            maxVisible={5}
            onSkillClick={onSkillClick ? (skill) => {
              // Stop propagation to prevent card click
              onSkillClick(skill);
            } : undefined}
          />
        </div>
      )}

      {/* Client Info */}
      <ClientInfoRow
        paymentVerified={job.client?.verificationStatus === 'VERIFIED' || job.paymentVerified}
        rating={job.client?.rating}
        reviewCount={job.client?.totalReviews}
        totalSpent={job.client?.totalSpent || job.clientSpent}
        location={job.client?.location?.country || job.location}
      />

      {/* Proposal Count */}
      {proposalCount && (
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Users className="w-3 h-3" />
          Proposals: {proposalCount}
        </div>
      )}
    </div>
  );
};

export default JobCard;
